import { createSlice, createAsyncThunk } from "@reduxjs/toolkit"
import { AI_BASE_URL } from "../../lib/aiApiBaseUrl"
import { apiRequest } from "../../lib/request"

/**
 * GET {AI_BASE_URL}/api/inbox/threads
 *
 * Fetches all threads (or a filtered subset) with summary counts.
 * Called on page load, tab/filter changes, and after any state mutation.
 */
export const fetchInboxThreads = createAsyncThunk(
  "inboxThreads/fetch",
  async (filters = {}, { rejectWithValue }) => {
    try {
      const params = new URLSearchParams()

      if (filters.query) params.append("query", filters.query)
      if (filters.triage) params.append("triage", filters.triage)
      if (filters.sentiment && filters.sentiment !== "all") {
        params.append("sentiment", filters.sentiment)
      }
      if (filters.activity_only) params.append("activity_only", "true")
      if (filters.assigned_only) params.append("assigned_only", "true")
      if (filters.channel) params.append("channel", filters.channel)
      if (filters.property) params.append("property", filters.property)
      if (filters.location) params.append("location", filters.location)
      if (filters.msg_type) params.append("msg_type", filters.msg_type)
      if (filters.time_range && filters.time_range !== "all") {
        params.append("time_range", filters.time_range)
      }
      if (filters.customer_type) params.append("customer_type", filters.customer_type)
      if (filters.since_ms) params.append("since_ms", filters.since_ms)
      if (filters.sort_by) params.append("sort_by", filters.sort_by)
      if (filters.sort_order) params.append("sort_order", filters.sort_order)
      if (filters.limit) params.append("limit", filters.limit)
      if (filters.force_refresh) params.append("force_refresh", "true")

      const qs = params.toString()
      const url = `${AI_BASE_URL}/api/inbox/threads${qs ? `?${qs}` : ""}`
      const data = await apiRequest(url, { method: "GET" })
      return data
    } catch (err) {
      return rejectWithValue(err?.message || "Failed to fetch inbox threads")
    }
  }
)

const initialState = {
  results: [],
  summary: {
    total_threads: 0,
    grand_total: 0,
    archived: 0,
    reply_now: 0,
    needs_property_help: 0,
    waiting_on_property: 0,
    property_responded: 0,
    positive: 0,
    neutral: 0,
    negative: 0,
    new_activity: 0,
  },
  count: 0,
  total: 0,
  available_filters: {
    channels: [],
    properties: [],
    msg_types: [],
  },
  source: null,
  status: "idle",
  error: null,
}

const inboxThreadsSlice = createSlice({
  name: "inboxThreads",
  initialState,
  reducers: {
    clearInboxThreads: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchInboxThreads.pending, (state) => {
        state.status = "loading"
        state.error = null
      })
      .addCase(fetchInboxThreads.fulfilled, (state, action) => {
        state.status = "succeeded"
        const d = action.payload || {}
        state.results = d.results || []
        state.summary = { ...initialState.summary, ...(d.summary || {}) }
        state.count = d.count ?? 0
        state.total = d.total ?? 0
        state.available_filters = d.available_filters || initialState.available_filters
        state.source = d.source ?? null
      })
      .addCase(fetchInboxThreads.rejected, (state, action) => {
        state.status = "failed"
        state.error = action.payload
      })
  },
})

export const { clearInboxThreads } = inboxThreadsSlice.actions
export default inboxThreadsSlice.reducer
